import { useEffect, useMemo, useState } from "react";
import { intl } from "LocaleWrapper";
import { useLazyQuery } from "@apollo/client";

import { Card } from "@cofano/cck";

import { Box, CircularProgress, Grid2 as Grid } from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import WaterDrop from "@mui/icons-material/WaterDrop";

import ShipTanksDetails from "view/ships/components/details/ShipTanksDetails";
import CargoTankAssignmentDialog from "view/voyages/components/cargotanks/CargoTankAssignmentDialog";

import { VoyageTankListCargoFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";
import { GetShipDocument } from "__generated__/view/ships/graphql/ship.queries.generated";

interface TankListProps {
	voyageId: number;
	shipId: number;
	selectedCargo?: { id?: number; };
	mayAssign?: boolean;
	cargos: VoyageTankListCargoFragment[];
	refetch: () => void;
}

const TankList = ({ voyageId, shipId, selectedCargo, mayAssign, cargos, refetch }: TankListProps) => {
	const [ dialogOpen, setDialogOpen ] = useState(false);
	const [ getShip, { data, loading } ] = useLazyQuery(GetShipDocument);

	const cargosPerTank = useMemo(() =>
		cargos.reduce((acc, cargo, index) => {
			cargo.tanks?.forEach(({ id }) => {
				(acc[id] ??= []).push({ cargo: cargo, index });
			});
			return acc;
		}, {} as Record<number, { cargo: VoyageTankListCargoFragment; index: number }[]>),
	[ cargos ],
	);

	useEffect(() => {
		if (shipId) {
			getShip({ variables: { id: Number(shipId) } });
		}
	}, [ shipId ]);

	if (loading) {
		return (
			<Grid size={12}>
				<Box sx={{ display: "flex", justifyContent: "center" }}>
					<CircularProgress />
				</Box>
			</Grid>
		);
	}

	if (!data?.ship) {
		return <></>;
	}

	if (data?.ship.tanks == null || data?.ship.tanks.length === 0) {
		return <></>;
	}

	const selectedCargoFull = cargos.find(c => c.id === selectedCargo?.id) ?? null;

	return (
		<Card
			disablePadding
			icon={<WaterDrop fontSize="small" color="primary" />}
			title={intl.formatMessage({ id: "TANKS" })}
			subtitle={intl.formatMessage({ id: `SHIP_${data.ship.tankProperties?.tankConfiguration}` })}
			actions={[ {
				icon: <Edit fontSize="small" />,
				onClick: () => setDialogOpen(true),
				hidden: !selectedCargoFull || !(mayAssign ?? true),
			} ]}>
			<ShipTanksDetails
				tanks={data.ship.tanks}
				tankConfiguration={data.ship.tankProperties?.tankConfiguration ?? null}
				cargos={cargosPerTank}
				selectedCargo={selectedCargo}
				showCargos={true} />

			{selectedCargoFull && (
				<CargoTankAssignmentDialog
					voyageId={voyageId}
					shipId={shipId}
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					refetch={refetch}
					cargo={selectedCargoFull} />
			)}
		</Card>
	);
};

export default TankList;
